export default function JoplinTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Joplin &amp; Tri-State Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Joplin-area travelers actually move helps us anticipate the passport urgencies that arise here. From cross-border business trips to medical tourism, the Tri-State region has unique travel patterns that create distinct passport timing pressures.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Nearest Major Airports to Joplin</h3>
                <p className="text-gray-400 text-xs">JLN, XNA, MCI — all within driving distance</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Joplin Regional Airport (JLN) offers regional connections, but most Tri-State international travelers drive to Joplin and fly out of XNA (Northwest Arkansas Regional, 70 miles south) or MCI (Kansas City International, 90 miles north). This layered airport situation means passport urgency often surfaces with a departure window of days — not the weeks standard processing requires.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking any international connection through XNA or MCI. Many destinations require six months validity beyond your return date — discovered at the last minute, this is devastating for business travel schedules.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-briefcase-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Mexico &amp; Canada Cross-Border Business</h3>
                <p className="text-gray-400 text-xs">Tri-State industrial and agricultural trade</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Joplin's manufacturing base — including metal fabrication, food processing, and distribution operations — generates regular business travel to Mexico and Canada for supplier visits, client meetings, and equipment inspections. Agriculture-related travel for livestock shows, commodity exchanges, and export documentation trips creates seasonal passport demand spikes in spring and fall when grain and cattle markets are most active.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Mexico requires passports for all U.S. air travelers. Canada requires passports for air travel and strongly recommends them for land crossings. Agricultural trade professionals should keep passports current and renewed well ahead of peak buying and selling seasons.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-heart-pulse-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Medical Tourism &amp; Healthcare Travel</h3>
                <p className="text-gray-400 text-xs">Freeman Health, Mercy Joplin regional hub</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Joplin's status as a regional medical hub — anchored by Freeman Health System and Mercy Hospital Joplin — draws healthcare professionals who travel for continuing education, international conferences, and collaborative programs. Patients and family members occasionally travel internationally for specialized treatment or participate in clinical exchange programs. Medical travel urgency can surface suddenly and unpredictably.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Medical emergencies abroad require a valid passport. If a family member needs emergency travel due to a health crisis, we can expedite passports on urgent timelines. Contact us immediately — we regularly handle same-week processing for documented medical situations.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MSSU Study Abroad &amp; Student Travel</h3>
                <p className="text-gray-400 text-xs">Missouri Southern State University, Joplin</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Missouri Southern State University sends students abroad through exchange programs and partnerships across Europe, Latin America, and Asia. Many students are first-time passport applicants — typically discovered after program acceptance, when the application deadline is already pressing. MSSU students studying in Spain, Germany, or Costa Rica need their passport before orientation, not after standard processing completes.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants (most MSSU students) require an in-person acceptance clerk visit at a Joplin-area post office or the Jasper County clerk's office. We guide you through the exact steps so you don't miss your program start date.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-route-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Route 66 Corridor &amp; Tourism Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Joplin sits on Historic Route 66 and attracts international tourists driving the legendary highway. International visitors occasionally discover passport or travel document issues here — and we can help travelers throughout the Tri-State area, not just Joplin residents. If you find yourself in a passport bind while traveling through the region, start your order online immediately.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}